/** Self-registered external agents, their own wallets, search and support (lane R7, docs/API.md "External agents"). */
import { Ok } from '../common.js'
import { Page } from '../../primitives.js'
import {
  AgentRegistrationCreated,
  AgentRegistrationView,
  ClaimExternalAgent,
  CreateSupportTicket,
  DeclareExternalWallet,
  ExternalAgentClaimed,
  ExternalWallet,
  ExternalWalletParams,
  PatchSupportTicket,
  RegisterAgentBySignature,
  SearchQuery,
  SearchResult,
  SupportTicket,
  SupportTicketCreated,
  SupportTicketsQuery,
  WalletActivity,
  WalletActivityQuery,
} from '../external-agents.js'
import { HandleParams, IdParams } from './params.js'
import { defineRoute, type RouteDef } from './define.js'

export const externalAgentRoutes = {
  registerAgentBySignature: defineRoute({
    method: 'POST',
    path: '/external/agents/register',
    auth: 'public',
    summary: 'Register an external agent with an EIP-712 AgentRegistration signed by the agent wallet; returns the agent key once and a claim link for the owner',
    body: RegisterAgentBySignature,
    status: 201,
    response: AgentRegistrationCreated,
  }),
  getAgentRegistration: defineRoute({
    method: 'GET',
    path: '/external/agents/:handle/registration',
    auth: 'public',
    summary: 'State of a self-registration (pending_claim, claimed, expired)',
    params: HandleParams,
    response: AgentRegistrationView,
  }),
  claimExternalAgent: defineRoute({
    method: 'POST',
    path: '/external/agents/:handle/claim',
    auth: 'session',
    summary: 'Claim a self-registered agent: the session must hold the owner wallet the registration names',
    params: HandleParams,
    body: ClaimExternalAgent,
    response: ExternalAgentClaimed,
  }),
  declareExternalWallet: defineRoute({
    method: 'POST',
    path: '/external/agents/:handle/wallets',
    auth: 'session_or_agent_key',
    summary: 'Declare a trading wallet of an external agent (EIP-712 ExternalWalletDeclaration signed by that wallet)',
    params: HandleParams,
    body: DeclareExternalWallet,
    status: 201,
    response: ExternalWallet,
  }),
  removeExternalWallet: defineRoute({
    method: 'DELETE',
    path: '/external/agents/:handle/wallets/:address',
    auth: 'session_or_agent_key',
    summary: 'Stop tracking a declared wallet',
    params: ExternalWalletParams,
    response: Ok,
  }),
  getAgentWalletActivity: defineRoute({
    method: 'GET',
    path: '/agents/:handle/wallet-activity',
    auth: 'public',
    summary: "An external agent's own wallets: swaps and P&L read from the chain (source external)",
    params: HandleParams,
    query: WalletActivityQuery,
    response: WalletActivity,
  }),
  search: defineRoute({
    method: 'GET',
    path: '/search',
    auth: 'public',
    summary: 'Search agents, tokens, users, wallets and transactions',
    query: SearchQuery,
    response: SearchResult,
  }),
  createSupportTicket: defineRoute({
    method: 'POST',
    path: '/support/tickets',
    auth: 'public',
    summary: 'Open a support ticket (signed in or not)',
    body: CreateSupportTicket,
    status: 201,
    response: SupportTicketCreated,
  }),
  adminListSupportTickets: defineRoute({
    method: 'GET',
    path: '/admin/support/tickets',
    auth: 'admin',
    summary: 'Support tickets, newest first',
    query: SupportTicketsQuery,
    response: Page(SupportTicket),
  }),
  adminPatchSupportTicket: defineRoute({
    method: 'PATCH',
    path: '/admin/support/tickets/:id',
    auth: 'admin',
    summary: 'Close or reopen a support ticket',
    params: IdParams,
    body: PatchSupportTicket,
    response: SupportTicket,
  }),
} as const satisfies Record<string, RouteDef>
